import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { Navbar, Footer } from "@/components/site-chrome";

const EASE = [0.16, 1, 0.3, 1] as const;
const FOREST = "#0D4F4A";
const FOREST_DEEP = "#083B3A";
const FOREST_MID = "#2F7E67";
const GOLD = "#D9A441";
const INK = "#101511";
const PAPER = "#F5F0E3";
const LINE = "#D5DED4";
const MUTED = "#647774";
const FS = "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif";

const dashboards = [
  { label: "Collections", value: "USD 1.2B", change: "+18%", color: GOLD, bars: [45, 62, 58, 71, 80, 92, 88, 95], detail: "Term 2 fee collection tracking with auto-reconciliation across all payment rails." },
  { label: "Attendance", value: "96.2%", change: "+2.1%", color: FOREST_MID, bars: [92, 94, 93, 95, 96, 97, 95, 96], detail: "Real-time attendance across all classes, with auto-confirmed GPS and NFC check-ins." },
  { label: "Performance", value: "87% avg", change: "+5%", color: "#F59E0B", bars: [72, 75, 78, 80, 82, 84, 85, 87], detail: "Grade trends across all subjects, with AI-flagged students needing intervention." },
  { label: "Expenditure", value: "USD 340M", change: "-3%", color: "#0EA5E9", bars: [85, 78, 72, 68, 65, 62, 60, 58], detail: "Procurement, feeding, transport and maintenance spend — tracked to the shilling." },
];

const channels = [
  { name: "WhatsApp", pct: 78, color: "#25D366" },
  { name: "SMS", pct: 92, color: "#007AFF" },
  { name: "Push", pct: 68, color: FOREST },
  { name: "In-App", pct: 85, color: GOLD },
];

export function AnalyticsPage() {
  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: PAPER, fontFamily: FS }}>
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: `linear-gradient(90deg, ${FOREST}, ${GOLD}, ${FOREST})` }} />
      <div className="relative z-10">
        <Navbar />
        <AnalyticsHero />
        <DashboardSection />
        <ChannelSection />
      </div>
      <Footer />
    </div>
  );
}

function AnalyticsHero() {
  return (
    <section className="relative overflow-hidden px-4 pb-12 pt-28 sm:px-8 sm:pt-40 sm:pb-20" style={{ background: FOREST_DEEP }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: `linear-gradient(${FOREST_MID} 1px, transparent 1px), linear-gradient(90deg, ${FOREST_MID} 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />
      <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${FOREST_MID}20 0%, transparent 60%)` }} />
      <div className="relative mx-auto max-w-6xl px-4">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: "rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.04)" }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: "rgba(255,255,255,0.7)" }}>Analytics</span>
        </div>
        <motion.h2 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: "#fff", letterSpacing: "-0.04em", lineHeight: 1.02 }}>See everything.</motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6, ease: EASE }} className="text-3xl font-bold tracking-tight sm:text-5xl" style={{ color: GOLD, letterSpacing: "-0.04em", lineHeight: 1.02, fontStyle: "italic" }}>Miss nothing.</motion.p>
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6, ease: EASE }} className="mt-4 max-w-xl text-sm leading-relaxed sm:mt-6 sm:text-lg sm:leading-relaxed" style={{ color: "rgba(255,255,255,0.5)" }}>Real-time dashboards that surface what matters — collections, attendance, performance, expenditure — before it becomes a problem.</motion.p>
      </div>
    </section>
  );
}

function DashboardSection() {
  const [activeDash, setActiveDash] = useState(0);
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const dash = dashboards[activeDash];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"];

  return (
    <section className="relative py-16 sm:py-24" style={{ background: FOREST_DEEP }}>
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `linear-gradient(${FOREST_MID} 1px, transparent 1px), linear-gradient(90deg, ${FOREST_MID} 1px, transparent 1px)`, backgroundSize: "40px 40px" }} />
      <div className="relative mx-auto max-w-5xl px-4 sm:px-8">
        <div className="mb-6 flex flex-wrap gap-2">
          {dashboards.map((d, i) => (
            <button key={d.label} onClick={() => setActiveDash(i)} className="rounded-full px-4 py-2 text-sm font-medium transition-all" style={{ background: activeDash === i ? `${d.color}15` : "rgba(255,255,255,0.03)", color: activeDash === i ? d.color : "rgba(255,255,255,0.5)", border: `1px solid ${activeDash === i ? `${d.color}30` : "rgba(255,255,255,0.06)"}` }}>{d.label}</button>
          ))}
        </div>
        <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr] lg:gap-8">
          <motion.div key={activeDash} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="rounded-2xl border p-6" style={{ borderColor: `${dash.color}20`, background: `${dash.color}06` }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.4)" }}>{dash.label}</p>
                <p className="mt-1 text-3xl font-bold tabular-nums" style={{ color: dash.color }}>{dash.value}</p>
              </div>
              <div className="text-right">
                <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${dash.color}15`, color: dash.color }}>{dash.change}</span>
                <p className="mt-1 text-[9px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.3)" }}>vs last term</p>
              </div>
            </div>
            <div className="mt-6 flex items-end justify-between gap-2" style={{ height: 180 }}>
              {dash.bars.map((bar, i) => (
                <div key={i} className="flex flex-1 flex-col items-center gap-2">
                  <div className="relative flex w-full flex-1 items-end">
                    <motion.div className="w-full rounded-t-lg" style={{ background: hoveredBar === i ? dash.color : `${dash.color}80` }} initial={{ height: 0 }} animate={{ height: `${bar}%` }} transition={{ delay: i * 0.05, duration: 0.5, ease: EASE }} onMouseEnter={() => setHoveredBar(i)} onMouseLeave={() => setHoveredBar(null)} />
                    {hoveredBar === i && (
                      <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="absolute -top-7 left-1/2 -translate-x-1/2 rounded-lg px-2 py-1 text-[10px] font-bold whitespace-nowrap" style={{ background: dash.color, color: "#fff" }}>{bar}%</motion.div>
                    )}
                  </div>
                  <span className="text-[9px] tabular-nums" style={{ color: "rgba(255,255,255,0.3)" }}>{months[i]}</span>
                </div>
              ))}
            </div>
          </motion.div>
          <div className="flex flex-col justify-center">
            <AnimatePresence mode="wait">
              <motion.div key={activeDash} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.25 }}>
                <h3 className="text-lg font-bold" style={{ color: "#fff", letterSpacing: "-0.02em" }}>{dash.label} Dashboard</h3>
                <p className="mt-2 text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.55)" }}>{dash.detail}</p>
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {[{ label: "This week", value: dash.bars[7] + "%", color: dash.color }, { label: "Last week", value: dash.bars[6] + "%", color: "rgba(255,255,255,0.4)" }, { label: "Trend", value: dash.change, color: dash.color }, { label: "Target", value: "95%", color: "rgba(255,255,255,0.4)" }].map(s => (
                    <div key={s.label} className="rounded-xl border p-3" style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
                      <p className="text-sm font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
                      <p className="text-[9px] uppercase tracking-wider" style={{ color: "rgba(255,255,255,0.3)" }}>{s.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function ChannelSection() {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), 300); return () => clearTimeout(t); }, []);

  return (
    <section className="relative py-16 sm:py-24" style={{ background: PAPER }}>
      <div className="mx-auto max-w-5xl px-4 sm:px-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: `${FOREST}20`, background: `${FOREST}06` }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: GOLD }} />
          <span className="text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: FOREST }}>Engagement</span>
        </div>
        <h2 className="text-2xl font-bold tracking-tight sm:text-4xl" style={{ color: INK, letterSpacing: "-0.04em", lineHeight: 1.05 }}>Every channel, <span style={{ color: FOREST, fontStyle: "italic" }}>one view.</span></h2>
        <div className="mt-10 grid gap-6 lg:grid-cols-2 lg:gap-8">
          <div className="rounded-2xl border bg-white p-6" style={{ borderColor: LINE }}>
            <p className="mb-4 text-xs font-medium uppercase tracking-wider" style={{ color: MUTED }}>Engagement by Channel</p>
            <div className="space-y-4">
              {channels.map((c, i) => (
                <div key={c.name}>
                  <div className="mb-1.5 flex justify-between text-xs"><span className="font-medium" style={{ color: INK }}>{c.name}</span><span style={{ color: MUTED }}>{c.pct}%</span></div>
                  <div className="h-2.5 overflow-hidden rounded-full" style={{ background: `${c.color}10` }}>
                    <motion.div className="h-full rounded-full" style={{ background: c.color }} initial={{ width: 0 }} animate={visible ? { width: `${c.pct}%` } : {}} transition={{ delay: i * 0.1, duration: 0.6, ease: EASE }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[{ label: "Messages sent", value: "2.4M", color: FOREST }, { label: "Delivery rate", value: "99.2%", color: GOLD }, { label: "Read rate", value: "87%", color: FOREST_MID }, { label: "Response time", value: "4.2 min", color: "#0EA5E9" }, { label: "Active parents", value: "38K", color: "#F59E0B" }, { label: "Broadcasts", value: "142", color: "#E11D48" }].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="rounded-xl border bg-white p-4" style={{ borderColor: LINE }}>
                <p className="text-2xl font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
                <p className="mt-1 text-[10px] uppercase tracking-wider" style={{ color: MUTED }}>{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default AnalyticsPage;
